import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { X, Save, RefreshCw, AlertCircle, Key } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Edit User Modal
 * Edit user details and force password reset
 */

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const t = useLang();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_e164: '',
    role: 'executor',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone_e164: user.phone_e164 || '',
        role: user.role || 'executor',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
      setErrors({});
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (formData.phone_e164 && !/^\+[1-9]\d{1,14}$/.test(formData.phone_e164)) {
      newErrors.phone_e164 = 'Invalid phone format (E.164)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix form errors');
      return;
    }

    try {
      setLoading(true);
            
      // Update user details
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${user.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        toast.success(t('admin.users.edit.success'));
        onSuccess && onSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin.users.edit.error'));
      }
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error(t('admin.users.edit.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordReset = async () => {
    try {
      setLoading(true);
      setShowResetConfirm(false);
            
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${user.user_id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Password reset! New credentials sent via WhatsApp');
        
        if (data.temp_password) {
          setTempPassword(data.temp_password);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(t('admin.users.edit.passwordMinLength') || 'Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
            
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${user.user_id}/set-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_password: newPassword,
            must_change_password: mustChangePassword
          })
        }
      );

      if (response.ok) {
        toast.success(t('admin.users.edit.passwordChanged') || 'Password changed successfully');
        setNewPassword('');
        setShowSetPassword(false);
        setMustChangePassword(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (err) {
      console.error('Error setting password:', err);
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: EMZI_SHADOWS.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: EMZI_COLORS.textPrimary,
            margin: 0
          }}>
            {t('admin.users.edit.title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: EMZI_COLORS.textSecondary
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {/* Full Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('admin.users.fullName')} *
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${errors.full_name ? '#ef4444' : EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => !errors.full_name && (e.target.style.borderColor = EMZI_COLORS.primary)}
              onBlur={(e) => e.target.style.borderColor = errors.full_name ? '#ef4444' : EMZI_COLORS.border}
            />
            {errors.full_name && (
              <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                {errors.full_name}
              </div>
            )}
          </div>

          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('admin.users.email')} *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${errors.email ? '#ef4444' : EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => !errors.email && (e.target.style.borderColor = EMZI_COLORS.primary)}
              onBlur={(e) => e.target.style.borderColor = errors.email ? '#ef4444' : EMZI_COLORS.border}
            />
            {errors.email && (
              <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                {errors.email}
              </div>
            )}
          </div>

          {/* Phone Number */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('admin.users.phoneNumber')}
            </label>
            <input
              type="text"
              value={formData.phone_e164}
              onChange={(e) => setFormData({ ...formData, phone_e164: e.target.value })}
              placeholder="+60123456789"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${errors.phone_e164 ? '#ef4444' : EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => !errors.phone_e164 && (e.target.style.borderColor = EMZI_COLORS.primary)}
              onBlur={(e) => e.target.style.borderColor = errors.phone_e164 ? '#ef4444' : EMZI_COLORS.border}
            />
            {errors.phone_e164 && (
              <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                {errors.phone_e164}
              </div>
            )}
          </div>

          {/* Role */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '8px'
            }}>
              {t('admin.users.role')} *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="admin">{t('admin.users.roles.admin')}</option>
              <option value="management_functional">{t('admin.users.roles.management_functional')}</option>
              <option value="supervisor">{t('admin.users.roles.supervisor')}</option>
              <option value="executor">{t('admin.users.roles.executor')}</option>
            </select>
          </div>

          {/* Status Toggle */}
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            background: EMZI_COLORS.surface,
            borderRadius: EMZI_RADIUS.md,
            border: `1px solid ${EMZI_COLORS.border}`
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary
                }}>
                  {t('admin.users.active')}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: EMZI_COLORS.textSecondary,
                  marginTop: '2px'
                }}>
                  User can login and access the system
                </div>
              </div>
            </label>
          </div>

          {/* Force Password Reset */}
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            background: '#fef3c7',
            borderRadius: EMZI_RADIUS.md,
            border: '1px solid #fbbf24'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <AlertCircle size={20} color="#f59e0b" />
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#92400e',
                  marginBottom: '4px'
                }}>
                  {t('admin.users.edit.forcePasswordReset')}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#78350f'
                }}>
                  Generate new temporary password and send via WhatsApp
                </div>
              </div>
            </div>

            {/* Temp password display */}
            {tempPassword && (
              <div style={{
                padding: '12px',
                background: '#fff',
                borderRadius: EMZI_RADIUS.sm,
                border: '1px solid #fbbf24',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#78350f', marginBottom: '4px', fontWeight: 600 }}>
                  New Temporary Password:
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#92400e',
                  fontFamily: 'monospace',
                  letterSpacing: '1px'
                }}>
                  {tempPassword}
                </div>
                <div style={{ fontSize: '11px', color: '#a16207', marginTop: '6px' }}>
                  This has been sent to the user via WhatsApp.
                </div>
              </div>
            )}

            {/* Confirmation inline */}
            {showResetConfirm ? (
              <div>
                <div style={{
                  fontSize: '13px',
                  color: '#92400e',
                  marginBottom: '10px',
                  fontWeight: 500
                }}>
                  {t('admin.users.edit.confirmPasswordReset')}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleForcePasswordReset}
                    disabled={loading}
                    style={{
                      padding: '8px 16px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    Yes, Reset
                  </button>
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    style={{
                      padding: '8px 16px',
                      background: '#fff',
                      color: '#78350f',
                      border: '1px solid #fbbf24',
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setTempPassword(null); setShowResetConfirm(true); }}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.sm,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <RefreshCw size={16} />
                Reset Password
              </button>
            )}
          </div>

          {/* Admin Set Password Directly */}
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            background: '#eff6ff',
            borderRadius: EMZI_RADIUS.md,
            border: '1px solid #93c5fd'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <Key size={20} color="#2563eb" />
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e40af',
                  marginBottom: '4px'
                }}>
                  {t('admin.users.edit.setPasswordTitle') || 'Set Password Directly'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#1d4ed8'
                }}>
                  {t('admin.users.edit.setPasswordDesc') || 'Set a specific password for this user'}
                </div>
              </div>
            </div>

            {showSetPassword ? (
              <div>
                <input
                  data-testid="set-password-input"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('admin.users.edit.newPasswordPlaceholder') || 'Enter new password (min 6 chars)'}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #93c5fd',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '14px',
                    marginBottom: '10px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#93c5fd'}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#1e40af'
                }}>
                  <input
                    type="checkbox"
                    checked={mustChangePassword}
                    onChange={(e) => setMustChangePassword(e.target.checked)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  {t('admin.users.edit.forceChangeOnLogin') || 'Force user to change password on next login'}
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    data-testid="set-password-save-btn"
                    onClick={handleSetPassword}
                    disabled={loading || !newPassword}
                    style={{
                      padding: '8px 16px',
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading || !newPassword ? 0.6 : 1
                    }}
                  >
                    {loading ? 'Saving...' : (t('admin.users.edit.savePassword') || 'Save Password')}
                  </button>
                  <button
                    onClick={() => { setShowSetPassword(false); setNewPassword(''); }}
                    style={{
                      padding: '8px 16px',
                      background: '#fff',
                      color: '#1e40af',
                      border: '1px solid #93c5fd',
                      borderRadius: EMZI_RADIUS.sm,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {t('admin.users.edit.cancel') || 'Cancel'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="set-password-btn"
                onClick={() => setShowSetPassword(true)}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.sm,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <Key size={16} />
                {t('admin.users.edit.changePassword') || 'Change Password'}
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: 'white',
                color: EMZI_COLORS.textPrimary,
                border: `2px solid ${EMZI_COLORS.border}`,
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                minHeight: '44px'
              }}
            >
              {t('admin.users.edit.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: EMZI_COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: EMZI_RADIUS.md,
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minHeight: '44px',
                opacity: loading ? 0.6 : 1
              }}
            >
              <Save size={18} />
              {loading ? 'Saving...' : t('admin.users.edit.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
